/**
 * Cálculos financieros puros de caja.
 * Sin dependencias de React — testeable de forma aislada.
 *
 * @param {Object}  params
 * @param {Array}   params.movimientos  ENTRADA / VENTA / SALIDA del día
 * @param {Array}   params.gastos       Gastos activos del día
 * @param {Array}   params.ajustes      Ajustes SOBRANTE / FALTANTE del día
 * @param {Array}   params.traspasos    Traspasos CAJ_A_QR / QR_A_CAJ del día
 * @returns {{ totalEfectivo: number, totalQR: number }}
 */
export const calcularEfectivoYQR = ({ movimientos, gastos, ajustes, traspasos }) => {
  let totalEfectivo = 0;
  let totalQR = 0;

  (movimientos || []).forEach(mov => {
    const monto = mov.cantidad * mov.precio;
    if (mov.tipo === 'ENTRADA') {
      if (mov.metodoPago === 'EFECTIVO' || !mov.metodoPago) totalEfectivo -= monto;
      else if (mov.metodoPago === 'QR') totalQR -= monto;
    } else if (mov.tipo === 'VENTA' || (mov.tipo === 'SALIDA' && mov.subTipo === 'VENTA')) {
      // Solo VENTA genera ingreso de dinero. SALIDA no.
      if (mov.metodoPago === 'EFECTIVO' || !mov.metodoPago) totalEfectivo += monto;
      else if (mov.metodoPago === 'QR') totalQR += monto;
    }
    // SALIDA: No afecta efectivo/QR. Solo disminuye inventario y genera costo.
  });

  (gastos || []).forEach(gasto => {
    if (gasto.metodoPago === 'EFECTIVO' || !gasto.metodoPago) totalEfectivo -= gasto.monto;
    else if (gasto.metodoPago === 'QR') totalQR -= gasto.monto;
  });

  (ajustes || []).forEach(ajuste => {
    const metodoPago = ajuste.metodoPago || 'EFECTIVO'; // Default EFECTIVO si no existe
    if (ajuste.tipo === 'SOBRANTE' || ajuste.tipo === 'INYECCIÓN') {
      if (metodoPago === 'EFECTIVO' || !ajuste.metodoPago) totalEfectivo += ajuste.monto;
      else if (metodoPago === 'QR') totalQR += ajuste.monto;
    } else if (ajuste.tipo === 'FALTANTE' || ajuste.tipo === 'RETIRO') {
      if (metodoPago === 'EFECTIVO' || !ajuste.metodoPago) totalEfectivo -= ajuste.monto;
      else if (metodoPago === 'QR') totalQR -= ajuste.monto;
    }
  });

  (traspasos || []).forEach(traspaso => {
    if (traspaso.direccion === 'CAJ_A_QR') {
      totalEfectivo -= traspaso.monto;
      totalQR += traspaso.monto;
    } else if (traspaso.direccion === 'QR_A_CAJ') {
      totalQR -= traspaso.monto;
      totalEfectivo += traspaso.monto;
    }
  });

  return { totalEfectivo, totalQR };
};

/**
 * Calcula los saldos acumulados de efectivo y QR hasta ANTES de una fecha dada.
 * Es decir, el cierre del día anterior = inicio del día seleccionado.
 *
 * @param {Object}  params
 * @param {Array}   params.movimientos  TODOS los movimientos históricos
 * @param {Array}   params.gastos       TODOS los gastos históricos
 * @param {Array}   params.ajustes      TODOS los ajustes históricos
 * @param {Array}   params.traspasos    TODOS los traspasos históricos
 * @param {string}  params.fechaLimite  Fecha YYYY-MM-DD (se excluye este día)
 * @param {Function} params.normalizarFecha  Función para normalizar fecha ISO
 * @returns {{ inicioEfectivo: number, inicioQR: number }}
 */
export const calcularSaldosAcumuladosHasta = ({ movimientos, gastos, ajustes, traspasos, fechaLimite, normalizarFecha }) => {
  const limite = fechaLimite; // YYYY-MM-DD

  const movAntes = (movimientos || []).filter(m => !m.anulado && normalizarFecha(m.fecha) < limite);
  const gastosAntes = (gastos || []).filter(g => g.estado !== 'ANULADO' && normalizarFecha(g.fecha) < limite);
  const ajustesAntes = (ajustes || []).filter(a => !a.anulado && normalizarFecha(a.fecha) < limite);
  const traspasosAntes = (traspasos || []).filter(t => !t.anulado && normalizarFecha(t.fecha) < limite);

  const { totalEfectivo, totalQR } = calcularEfectivoYQR({
    movimientos: movAntes,
    gastos: gastosAntes,
    ajustes: ajustesAntes,
    traspasos: traspasosAntes,
  });

  return { inicioEfectivo: totalEfectivo, inicioQR: totalQR };
};
